import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import orderBy from "lodash.orderby";

import { PermissionKey, PermissionScope } from "@fitness-app/data-models";
import {
  ExerciseRecordingStatus,
  type ExerciseRecordingWithExerciseInfo,
} from "@fitness-app/data-models/entities/ExerciseRecording";

import { usePermission } from "~/hooks/usePermission";
import { useUserRole } from "~/hooks/trainer/useUserRole";
import { supabase } from "~/store/initializeStore";

export const useExerciseRecordings = () => {
  const [statusFilter, changeStatusFilter] = useState([ExerciseRecordingStatus.RECORDED]);
  const { userId } = useUserRole();
  // Kolejka nagrań całego konta należy do tego, kto zarządza programami w
  // zakresie tenanta; reszta widzi wyłącznie nagrania przypisane do siebie.
  // Wcześniej filtr `userId` zakładany był po roli ACCOUNT_OWNER, więc tylko
  // to konto widziało wycinek, a operacyjne — cokolwiek dało RLS.
  const reviewsWholeAccount = usePermission(PermissionKey.PROGRAMS_MANAGE, PermissionScope.ALL);
  const { data, isLoading } = useQuery(["exercise_recordings", statusFilter, reviewsWholeAccount, userId], {
    queryFn: async () => {
      const query = supabase
        .from("exercise_recording_ownership")
        .select(
          reviewsWholeAccount
            ? "*, recording:exerciseRecordingId!inner(*,trainee:traineeId(firstName,lastName,email,trainer:assignedTrainerId(email,userId,firstName,lastName)),exercise:parentExerciseId(name),plan:programId(name))"
            : "*, recording:exerciseRecordingId!inner(*,trainee:traineeId(firstName,lastName,email),exercise:parentExerciseId(name),plan:programId(name))",
        )
        .in("recording.status", statusFilter);

      if (!reviewsWholeAccount) {
        query.eq("userId", userId);
      }

      const { error, data } = await query.limit(300).returns<{ recording: ExerciseRecordingWithExerciseInfo }[]>();
      if (error) {
        return [];
      }
      return data;
    },
  });

  return {
    data: orderBy(data?.map((item) => item.recording) || [], "scheduledAt", "desc"),
    isLoading,
    count: data?.length ? data.filter((item) => item.recording.status === ExerciseRecordingStatus.RECORDED).length : 0,
    statusFilter,
    changeStatusFilter,
  };
};
